import { X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './button';
import { cn } from '@/lib/utils';

/**
 * Ferme sur Échap et rend le fond inerte tant que la couche est ouverte.
 *
 * Les deux effets sont séparés à dessein. Le gestionnaire d'Échap change
 * d'identité dès que le formulaire devient « modifié » ; s'il pilotait aussi la
 * prise de focus, celle-ci se rejouerait en pleine frappe et renverrait le
 * curseur au premier champ — le champ perdait le focus à la première lettre.
 * La mise au point n'a lieu qu'à l'ouverture ; l'abonnement clavier, lui, passe
 * par une référence pour rester stable tout en appelant toujours la dernière
 * logique.
 */
const pileCouches: symbol[] = [];

/**
 * Vrai si une couche modale est ouverte.
 *
 * Sert aux raccourcis globaux : ouvrir la palette de recherche par-dessus un
 * formulaire en cours de saisie inviterait à naviguer ailleurs, donc à perdre
 * la saisie. Le raccourci se tait tant qu'une couche est ouverte.
 */
export function coucheModaleOuverte(): boolean {
  return pileCouches.length > 0;
}

export function useCoucheModale(ouvert: boolean, onEchap: () => void) {
  const conteneur = useRef<HTMLDivElement>(null);

  const echap = useRef(onEchap);
  echap.current = onEchap;

  useEffect(() => {
    if (!ouvert) return;

    /* Les couches peuvent s'empiler — créer un client depuis le formulaire de
     * commande, par exemple. Seule celle du dessus répond à Échap, sinon une
     * seule touche refermerait aussi le formulaire qui l'a ouverte. */
    const jeton = Symbol('couche');
    pileCouches.push(jeton);

    const surTouche = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (pileCouches[pileCouches.length - 1] !== jeton) return;
      echap.current();
    };
    document.addEventListener('keydown', surTouche);

    return () => {
      document.removeEventListener('keydown', surTouche);
      const i = pileCouches.lastIndexOf(jeton);
      if (i !== -1) pileCouches.splice(i, 1);
    };
  }, [ouvert]);

  useEffect(() => {
    if (!ouvert) return;

    const debordement = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Le premier champ prend le focus : la saisie enchaîne sans passer par la
    // souris. Une seule fois, à l'ouverture.
    const cible = conteneur.current?.querySelector<HTMLElement>(
      'input:not([type="hidden"]), select, textarea, button',
    );
    cible?.focus();

    return () => {
      document.body.style.overflow = debordement;
    };
  }, [ouvert]);

  return conteneur;
}

export type DialogProps = {
  ouvert: boolean;
  onFermer: () => void;
  titre: string;
  description?: string;
  taille?: keyof typeof TAILLES;
  pied?: ReactNode;
  /**
   * Vrai quand la saisie en cours n'est pas enregistrée. Les fermetures
   * accidentelles — Échap, clic hors du cadre, croix — demandent alors
   * confirmation. Un formulaire intact se ferme sans rien demander.
   */
  modifie?: boolean;
  children: ReactNode;
};

/**
 * Garde-fou contre la perte de saisie.
 *
 * Un formulaire de commande compte quinze champs ; Échap ou un clic à côté
 * suffisait à tout effacer sans un mot. Les deux boutons sont volontairement
 * dissymétriques : continuer la saisie est l'action sûre et prend le focus,
 * fermer sans enregistrer est marqué comme destructeur.
 */
function ConfirmationAbandon({
  onContinuer,
  onAbandonner,
}: {
  onContinuer: () => void;
  onAbandonner: () => void;
}) {
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-label="Modifications non enregistrées"
      className="absolute inset-0 z-10 flex items-center justify-center bg-ink/40 p-4"
    >
      <div className="w-full max-w-sm rounded-card border border-line bg-white p-5 shadow-pop">
        <p className="text-sm font-semibold text-ink">Des modifications ne sont pas enregistrées</p>
        <p className="mt-1 text-sm text-ink-muted">
          Fermer maintenant les perdrait. Rien n'a encore été envoyé.
        </p>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={onContinuer} autoFocus>
            Continuer la saisie
          </Button>
          <Button variant="danger" onClick={onAbandonner}>
            Fermer sans enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
}

const TAILLES = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
  /* Presque tout l'écran, pour un formulaire qui doit se lire d'un bloc. La
   * borne à 1 440 px évite des lignes de saisie démesurées sur un grand
   * moniteur, où l'œil ne ferait plus le lien entre étiquette et valeur. */
  plein: 'max-w-[1440px]',
} as const;

export function Dialog({
  ouvert,
  onFermer,
  titre,
  description,
  taille = 'md',
  pied,
  modifie,
  children,
}: DialogProps) {
  const [confirmation, setConfirmation] = useState(false);

  // Une réouverture repart d'un état propre.
  useEffect(() => {
    if (!ouvert) setConfirmation(false);
  }, [ouvert]);

  const demanderFermeture = useCallback(() => {
    if (modifie) {
      setConfirmation(true);
      return;
    }
    onFermer();
  }, [modifie, onFermer]);

  /* Échap referme d'abord la demande de confirmation : sinon la touche qui sert
   * à revenir en arrière serait aussi celle qui abandonne la saisie. */
  const surEchap = useCallback(() => {
    if (confirmation) {
      setConfirmation(false);
      return;
    }
    demanderFermeture();
  }, [confirmation, demanderFermeture]);

  const conteneur = useCoucheModale(ouvert, surEchap);
  if (!ouvert) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:items-center">
      <div
        className="fixed inset-0 animate-fade-in bg-ink/40"
        onClick={demanderFermeture}
        aria-hidden="true"
      />
      {/* Colonne bornée à la hauteur de l'écran : seul le corps défile, le titre
          et les boutons restent visibles. Un formulaire long — la commande en
          compte une trentaine de champs — emportait sinon « Enregistrer » hors
          de l'écran, sans rien pour indiquer qu'il fallait remonter. */}
      <div
        ref={conteneur}
        role="dialog"
        aria-modal="true"
        aria-label={titre}
        className={cn(
          'relative flex max-h-[calc(100dvh-2rem)] w-full flex-col animate-slide-up',
          'rounded-card border border-line bg-white shadow-pop',
          TAILLES[taille],
        )}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-ink">{titre}</h2>
            {description ? <p className="mt-0.5 text-sm text-ink-muted">{description}</p> : null}
          </div>
          <Button variant="ghost" size="icon" onClick={demanderFermeture} aria-label="Fermer">
            <X aria-hidden className="h-4 w-4" />
          </Button>
        </div>

        {/* `min-h-0` est indispensable : sans lui, un enfant flex refuse de
            rétrécir sous sa hauteur de contenu et la barre n'apparaît jamais. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {pied ? (
          <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-line bg-surface-sunken px-5 py-3">
            {pied}
          </div>
        ) : null}

        {confirmation ? (
          <ConfirmationAbandon
            onContinuer={() => setConfirmation(false)}
            onAbandonner={() => {
              setConfirmation(false);
              onFermer();
            }}
          />
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

/** Panneau latéral — utilisé pour le détail d'une commande. */
export function Drawer({
  ouvert,
  onFermer,
  titre,
  description,
  pied,
  children,
}: Omit<DialogProps, 'taille'>) {
  const conteneur = useCoucheModale(ouvert, onFermer);
  if (!ouvert) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className="fixed inset-0 animate-fade-in bg-ink/40"
        onClick={onFermer}
        aria-hidden="true"
      />
      <div
        ref={conteneur}
        role="dialog"
        aria-modal="true"
        aria-label={titre}
        className="relative flex h-full w-full max-w-2xl animate-slide-left flex-col border-l border-line bg-white shadow-pop"
      >
        <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-ink">{titre}</h2>
            {description ? <p className="mt-0.5 text-sm text-ink-muted">{description}</p> : null}
          </div>
          <Button variant="ghost" size="icon" onClick={onFermer} aria-label="Fermer">
            <X aria-hidden className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {pied ? (
          <div className="flex flex-wrap justify-end gap-2 border-t border-line bg-surface-sunken px-5 py-3">
            {pied}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

/** Confirmation avant une action irréversible. */
export function ConfirmDialog({
  ouvert,
  onFermer,
  onConfirmer,
  titre,
  message,
  libelleConfirmer = 'Confirmer',
  danger,
  chargement,
  children,
}: {
  ouvert: boolean;
  onFermer: () => void;
  onConfirmer: () => void;
  titre: string;
  message: ReactNode;
  libelleConfirmer?: string;
  danger?: boolean;
  chargement?: boolean;
  /** Champs complémentaires, par exemple le motif d'annulation. */
  children?: ReactNode;
}) {
  return (
    <Dialog
      ouvert={ouvert}
      onFermer={onFermer}
      titre={titre}
      taille="sm"
      pied={
        <>
          <Button variant="outline" onClick={onFermer} disabled={chargement}>
            Annuler
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            onClick={onConfirmer}
            chargement={chargement}
          >
            {libelleConfirmer}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm text-ink-soft">
        <div>{message}</div>
        {children}
      </div>
    </Dialog>
  );
}
